import { attachInput } from './input.js';
import { ViewerSession } from './rtc.js';
import { ScreenDecoder, isSupported as decoderSupported, type StreamHeader } from './decoder.js';
import { DeviceTerminal } from './terminal.js';
import { FileTransfer, formatBytes, iconFor, type Entry, type Listing } from './files.js';
import { AuditLog } from './audit.js';

const connectPanel = requireElement<HTMLElement>('connect-panel');
const sessionPanel = requireElement<HTMLElement>('session-panel');
const serverInput = requireElement<HTMLInputElement>('server');
const sessionInput = requireElement<HTMLInputElement>('session');
const pinInput = requireElement<HTMLInputElement>('pin');
const unattendedInput = requireElement<HTMLInputElement>('unattended');
const connectButton = requireElement<HTMLButtonElement>('connect');
const disconnectButton = requireElement<HTMLButtonElement>('disconnect');
const status = requireElement<HTMLElement>('status');
const sessionStatus = requireElement<HTMLElement>('session-status');
const video = requireElement<HTMLVideoElement>('screen');
const canvas = requireElement<HTMLCanvasElement>('screen-canvas');
const terminalPanel = requireElement<HTMLElement>('terminal-panel');
const terminalHost = requireElement<HTMLElement>('terminal');
const terminalButton = requireElement<HTMLButtonElement>('toggle-terminal');
const streamButton = requireElement<HTMLButtonElement>('toggle-stream');
const cameraButton = requireElement<HTMLButtonElement>('toggle-camera');
const cameraSwitchButton = requireElement<HTMLButtonElement>('switch-camera');
const micButton = requireElement<HTMLButtonElement>('toggle-mic');
const cameraPanel = requireElement<HTMLElement>('camera-panel');
const cameraVideo = requireElement<HTMLVideoElement>('camera');
const remoteAudio = requireElement<HTMLAudioElement>('remote-audio');
const connectionIndicator = requireElement<HTMLElement>('connection-indicator');
const stageIdle = requireElement<HTMLElement>('stage-idle');
const filesPanel = requireElement<HTMLElement>('files-panel');
const filePath = requireElement<HTMLInputElement>('file-path');
const fileList = requireElement<HTMLElement>('file-list');
const fileUpButton = requireElement<HTMLButtonElement>('file-up');
const fileRefreshButton = requireElement<HTMLButtonElement>('file-refresh');
const fileStatus = requireElement<HTMLElement>('file-status');
const auditButton = requireElement<HTMLButtonElement>('toggle-audit');
const auditPanel = requireElement<HTMLElement>('audit-panel');
const auditList = requireElement<HTMLElement>('audit-list');
const auditCount = requireElement<HTMLElement>('audit-count');
const auditExportButton = requireElement<HTMLButtonElement>('audit-export');
const auditClearButton = requireElement<HTMLButtonElement>('audit-clear');

let session: ViewerSession | null = null;
let detachInput: (() => void) | null = null;
let decoder: ScreenDecoder | null = null;
let terminal: DeviceTerminal | null = null;
let files: FileTransfer | null = null;
let audit: AuditLog | null = null;
let control: RTCDataChannel | null = null;
let screenStreaming = false;
let cameraStreaming = false;
let micStreaming = false;

/** A dev server on this machine, where the localhost defaults are the useful ones. */
const isLocalDev = ['localhost', '127.0.0.1', '::1'].includes(location.hostname);

/**
 * Where to reach signaling by default.
 *
 * A hosted viewer derives it from its own origin, matching the reverse-proxy layout in
 * infra/Caddyfile: same domain, `/ws`. So a deployed page needs no configuration, and
 * an https page never defaults to a `ws://` URL the browser would refuse as mixed
 * content. Locally it points at the dev server's port instead.
 */
function defaultServerUrl(): string {
  if (isLocalDev) return 'ws://localhost:8090';
  return `${location.protocol === 'https:' ? 'wss:' : 'ws:'}//${location.host}/ws`;
}

/**
 * Prefill, so iterating does not mean retyping a code and PIN on every reload.
 *
 * Precedence is query string, then last-used values in localStorage, then these
 * defaults. Query params win so a bookmark can pin a specific session without
 * disturbing saved values.
 *
 *   ?s=123456789&p=654321&url=wss://remote.example.com/ws&auto=1
 *
 * `auto=1` connects on load with no click at all.
 */
const DEFAULTS = {
  server: defaultServerUrl(),
  // The dev code and PIN match android/gradle.properties, and are prefilled only when
  // running locally: a hosted page should not ship credentials that look usable, least
  // of all now that a session grants a device shell.
  session: isLocalDev ? '123456789' : '',
  pin: isLocalDev ? '654321' : '',
};

const params = new URLSearchParams(location.search);

function prefill(input: HTMLInputElement, key: string, param: string, fallback: string): void {
  const value = params.get(param) ?? localStorage.getItem(`droid-remote.${key}`) ?? fallback;
  input.value = value;
}

prefill(serverInput, 'server', 'url', DEFAULTS.server);
prefill(sessionInput, 'session', 's', DEFAULTS.session);
prefill(pinInput, 'pin', 'p', DEFAULTS.pin);

// ?device=1 opens in unattended mode, so a bookmark can target an enrolled device.
unattendedInput.checked = params.get('device') === '1';

connectButton.addEventListener('click', start);

// Enter anywhere in the form submits, which is what people expect when reading a
// code off a phone.
for (const field of [serverInput, sessionInput, pinInput]) {
  field.addEventListener('keydown', (event) => {
    if (event.key === 'Enter') start();
  });
}

// The phone shows the code grouped 3-3-3 so it can be read aloud. Accept it typed
// either way rather than making people strip the spaces.
sessionInput.addEventListener('input', () => {
  const digits = sessionInput.value.replace(/\D/g, '').slice(0, 9);
  sessionInput.value = digits.replace(/(\d{3})(?=\d)/g, '$1 ').trim();
});

pinInput.addEventListener('input', () => {
  pinInput.value = pinInput.value.replace(/\D/g, '').slice(0, 6);
});

disconnectButton.addEventListener('click', () => session?.close());

/** Sends a source on/off request and shows a pending label until the host echoes state. */
function requestSource(kind: 'screen' | 'camera' | 'mic', on: boolean, extra: object = {}): void {
  if (control?.readyState !== 'open') return;
  control.send(JSON.stringify({ c: 'source', kind, on, ...extra }));
}

streamButton.addEventListener('click', () => {
  const want = !screenStreaming;
  requestSource('screen', want);
  streamButton.textContent = want ? 'Starting…' : 'Stopping…';
  streamButton.disabled = true;
});

cameraButton.addEventListener('click', () => {
  const want = !cameraStreaming;
  // Default to the back camera; Flip switches it once running.
  requestSource('camera', want, want ? { facing: 'back' } : {});
  cameraButton.disabled = true;
});

cameraSwitchButton.addEventListener('click', () => {
  if (control?.readyState === 'open') control.send(JSON.stringify({ c: 'switch-camera' }));
});

micButton.addEventListener('click', () => {
  const want = !micStreaming;
  requestSource('mic', want);
  micButton.disabled = true;
  // A click is a user gesture, so priming playback here lets the audio track autoplay when
  // it arrives a moment later after renegotiation.
  if (want) remoteAudio.play().catch(() => undefined);
});

terminalButton.addEventListener('click', () => {
  const showing = terminalPanel.classList.toggle('hidden');
  filesPanel.classList.toggle('hidden', showing);
  terminalButton.classList.toggle('active', !showing);
  if (!showing) {
    // Becoming visible: the container now has a size, so refit and take focus.
    terminal?.resize();
    terminal?.focus();
  }
});

auditButton.addEventListener('click', () => {
  const showing = !auditPanel.classList.toggle('hidden');
  auditButton.classList.toggle('active', showing);
  if (showing) auditList.scrollTop = auditList.scrollHeight;
});

auditExportButton.addEventListener('click', () => audit?.export());

auditClearButton.addEventListener('click', () => {
  if (!confirm('Clear the captured input log on the device? This cannot be undone.')) return;
  audit?.clear();
});

fileUpButton.addEventListener('click', () => files?.parent());
fileRefreshButton.addEventListener('click', () => files?.list(filePath.value.trim() || '/sdcard'));
filePath.addEventListener('keydown', (event) => {
  if (event.key === 'Enter') files?.list(filePath.value.trim() || '/sdcard');
});

/**
 * Renders a directory listing.
 *
 * Directories first, then names case-insensitively, which is what a file manager does and
 * makes a camera folder navigable. Rows are rebuilt wholesale: a listing is small and this
 * avoids any question of stale rows after a delete.
 */
function renderListing(listing: Listing): void {
  filePath.value = listing.path;
  fileList.replaceChildren();

  const sorted = [...listing.entries].sort((a, b) =>
    a.d !== b.d ? (a.d ? -1 : 1) : a.n.localeCompare(b.n, undefined, { sensitivity: 'base' }),
  );

  if (sorted.length === 0) {
    const empty = document.createElement('div');
    empty.className = 'file-empty';
    empty.textContent = 'Empty folder, or not readable by the shell user.';
    fileList.append(empty);
    return;
  }

  for (const entry of sorted) {
    fileList.append(buildRow(entry));
  }
}

function buildRow(entry: Entry): HTMLElement {
  const row = document.createElement('div');
  row.className = 'file-row';
  row.setAttribute('role', 'listitem');

  const icon = document.createElement('span');
  icon.className = 'file-icon';
  icon.textContent = iconFor(entry);

  // Clicking the name does the obvious thing for the type: open a folder, fetch a file.
  const name = document.createElement('button');
  name.className = 'file-name';
  name.textContent = entry.n;
  name.title = entry.n;
  name.addEventListener('click', () => {
    const path = files!.resolve(entry.n);
    if (entry.d) files!.list(path);
    else files!.requestDownload(path);
  });

  const meta = document.createElement('span');
  meta.className = 'file-meta';
  meta.textContent = entry.d ? describeDate(entry.m) : `${formatBytes(entry.s)} · ${describeDate(entry.m)}`;

  row.append(icon, name, meta);

  if (!entry.d) {
    const download = document.createElement('button');
    download.className = 'file-action';
    download.textContent = '⬇';
    download.title = `Download ${entry.n}`;
    download.addEventListener('click', () => files!.requestDownload(files!.resolve(entry.n)));
    row.append(download);
  }

  const remove = document.createElement('button');
  remove.className = 'file-action danger';
  remove.textContent = '🗑';
  remove.title = entry.d ? `Delete ${entry.n} (must be empty)` : `Delete ${entry.n}`;
  remove.addEventListener('click', () => {
    // There is no trash on the device, so a delete is final. Ask once, naming the target.
    if (!confirm(`Delete ${entry.n} on the device? This cannot be undone.`)) return;
    files!.requestDelete(files!.resolve(entry.n));
  });
  row.append(remove);

  return row;
}

/** Epoch seconds to something short. The device's clock, so no timezone gymnastics. */
function describeDate(seconds: number): string {
  if (!seconds) return '';
  return new Date(seconds * 1000).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

// Drag and drop over the whole panel. dragover must be cancelled or the browser navigates
// away to the dropped file instead of handing it over.
for (const type of ['dragenter', 'dragover'] as const) {
  filesPanel.addEventListener(type, (event) => {
    event.preventDefault();
    filesPanel.classList.add('dropping');
  });
}
filesPanel.addEventListener('dragleave', (event) => {
  // Only clear when the pointer actually left the panel, not on every child crossing.
  if (event.target === filesPanel) filesPanel.classList.remove('dropping');
});
filesPanel.addEventListener('drop', (event) => {
  event.preventDefault();
  filesPanel.classList.remove('dropping');
  const dropped = Array.from(event.dataTransfer?.files ?? []);
  if (dropped.length === 0) return;
  if (!files?.isReady) return setStatus(fileStatus, 'No file channel on this session.', true);
  if (files.isBusy) return setStatus(fileStatus, 'Another transfer is still running.', true);
  void files.upload(dropped);
});

for (const button of document.querySelectorAll<HTMLButtonElement>('[data-key]')) {
  // Keep keyboard focus on the render surface. Without this, clicking a nav key moves
  // focus to the button and subsequent keystrokes (e.g. a lock-screen PIN) go nowhere.
  // preventDefault on mousedown blocks the focus shift while still firing the click.
  button.addEventListener('mousedown', (event) => event.preventDefault());
  button.addEventListener('click', () => {
    const key = button.dataset.key;
    if (key) session?.sendInput({ t: 'key', a: key });
  });
}

function start(): void {
  const serverUrl = serverInput.value.trim();
  const sessionId = sessionInput.value.replace(/\D/g, '');
  const pin = pinInput.value.replace(/\D/g, '');
  const unattended = unattendedInput.checked;

  if (!serverUrl) return setStatus(status, 'Enter the signaling server address.', true);
  const codeLabel = unattended ? 'device code' : 'session code';
  if (sessionId.length !== 9) return setStatus(status, `The ${codeLabel} is 9 digits.`, true);
  if (pin.length !== 6) return setStatus(status, 'The PIN is 6 digits.', true);

  connectButton.disabled = true;
  setStatus(status, 'Connecting…');

  // Remember for next reload. Stored raw, without the display grouping.
  localStorage.setItem('droid-remote.server', serverUrl);
  localStorage.setItem('droid-remote.session', sessionId);
  localStorage.setItem('droid-remote.pin', pin);

  session = new ViewerSession({
    onTrack: (stream) => {
      video.srcObject = stream;
      // Autoplay only survives because the element is muted; there is no audio
      // track in this build, so muted costs nothing.
      void video.play().catch(() => setStatus(sessionStatus, 'Click the video to start playback.'));

      showSessionPanel();
      showRenderer('video');
      detachInput?.();
      detachInput = attachInput(
        video,
        () => ({ width: video.videoWidth, height: video.videoHeight }),
        (command) => session?.sendInput(command),
      );
      video.focus();
    },
    onVideoDataChannel: (channel) => {
      // scrcpy mode: no track, an H.264 stream on this channel decoded by WebCodecs.
      if (!decoderSupported()) {
        // WebCodecs is secure-context only, so a page served over plain http from
        // anything but localhost has no VideoDecoder at all. That looks identical to an
        // unsupported browser unless the difference is spelled out.
        setStatus(
          sessionStatus,
          window.isSecureContext
            ? 'This browser cannot decode the stream. Use Chrome or Edge.'
            : 'Serve this page over HTTPS: decoding needs WebCodecs, which browsers only allow in a secure context.',
          true,
        );
        return;
      }
      const activeDecoder = new ScreenDecoder(canvas, (message) =>
        setStatus(sessionStatus, message, true),
      );
      decoder = activeDecoder;
      channel.onmessage = (event) => {
        if (typeof event.data === 'string') {
          activeDecoder.handleHeader(JSON.parse(event.data) as StreamHeader);
        } else {
          activeDecoder.handleChunk(event.data as ArrayBuffer);
        }
      };

      showSessionPanel();
      showRenderer('canvas');
      detachInput?.();
      detachInput = attachInput(
        canvas,
        () => ({ width: activeDecoder.frameWidth, height: activeDecoder.frameHeight }),
        (command) => session?.sendInput(command),
      );
      canvas.focus();
    },
    onCameraTrack: (stream) => {
      cameraVideo.srcObject = stream;
      void cameraVideo.play().catch(() => undefined);
    },
    onMicTrack: (stream) => {
      remoteAudio.srcObject = stream;
      remoteAudio.play().catch(() =>
        setStatus(sessionStatus, 'Click Mic again to allow audio playback.', true),
      );
    },
    onControlDataChannel: (channel) => {
      control = channel;
      // The panel may not be visible until a media channel opens, but the control channel
      // opening is the moment the session is genuinely usable, so surface it now.
      showSessionPanel();
      channel.onmessage = (event) => {
        if (typeof event.data !== 'string') return;
        handleControl(event.data);
      };
      channel.onclose = () => {
        control = null;
      };
      updateSourceButtons();
    },
    onShellDataChannel: (channel) => {
      // The terminal exists for every session; the panel stays hidden until asked for.
      terminal = new DeviceTerminal(terminalHost);
      terminal.attach(channel);
    },
    onFilesDataChannel: (channel) => {
      files = new FileTransfer({
        onListing: renderListing,
        onStatus: (text, busy) => {
          setStatus(fileStatus, text, false);
          fileRefreshButton.disabled = busy;
          fileUpButton.disabled = busy;
        },
      });
      files.attach(channel);
      // Load a first listing now, so opening the panel shows something immediately.
      files.list('/sdcard');
    },
    onAuditDataChannel: (channel) => {
      audit = new AuditLog(auditList, {
        onCount: (n) => {
          auditCount.textContent = String(n);
        },
      });
      audit.attach(channel);
    },
    onStatus: (text, isError) => {
      const target = sessionPanel.classList.contains('hidden') ? status : sessionStatus;
      setStatus(target, text, isError);
      if (isError) connectButton.disabled = false;
    },
    onClosed: () => {
      teardown();
    },
  });

  session.connect(serverUrl, sessionId, pin, unattended);
}

/** Parses a host control frame: the connection indicator, source state, or a source error. */
function handleControl(text: string): void {
  let message: {
    c: string;
    backend?: string;
    input?: boolean;
    screen?: boolean;
    camera?: boolean;
    mic?: boolean;
    kind?: string;
    message?: string;
  };
  try {
    message = JSON.parse(text) as typeof message;
  } catch {
    return;
  }

  switch (message.c) {
    case 'hello':
      showConnection(message.backend, message.input);
      break;
    case 'state':
      screenStreaming = message.screen ?? false;
      cameraStreaming = message.camera ?? false;
      micStreaming = message.mic ?? false;
      updateSourceButtons();
      break;
    case 'error':
      setStatus(sessionStatus, message.message ?? 'A source failed to start.', true);
      updateSourceButtons();
      break;
  }
}

/** Renders the connection indicator from the host's `hello`. */
function showConnection(backend: string | undefined, input: boolean | undefined): void {
  connectionIndicator.classList.remove('adb', 'accessibility');
  if (backend === 'adb') {
    connectionIndicator.classList.add('adb');
    connectionIndicator.textContent = input ? 'Connected via adb (zero-touch)' : 'adb: link down';
  } else if (backend === 'accessibility') {
    connectionIndicator.classList.add('accessibility');
    connectionIndicator.textContent = input
      ? 'Connected via MediaProjection + Accessibility'
      : 'MediaProjection (input off: enable Accessibility)';
  } else {
    connectionIndicator.textContent = 'Connected';
  }
}

/** Reflects the current source states in the buttons, the idle placeholder, and the camera. */
function updateSourceButtons(): void {
  const open = control?.readyState === 'open';

  streamButton.disabled = !open;
  streamButton.textContent = screenStreaming ? 'Stop streaming' : 'Start streaming';
  streamButton.classList.toggle('active', screenStreaming);
  // The idle placeholder is only meaningful in the stage when no visual source is showing.
  stageIdle.classList.toggle('hidden', screenStreaming || cameraStreaming);

  cameraButton.disabled = !open;
  cameraButton.textContent = cameraStreaming ? 'Camera off' : 'Camera';
  cameraButton.classList.toggle('active', cameraStreaming);
  cameraSwitchButton.classList.toggle('hidden', !cameraStreaming);
  cameraPanel.classList.toggle('hidden', !cameraStreaming);
  if (!cameraStreaming) cameraVideo.srcObject = null;

  micButton.disabled = !open;
  micButton.textContent = micStreaming ? 'Mic off' : 'Mic';
  micButton.classList.toggle('active', micStreaming);
  if (!micStreaming) remoteAudio.srcObject = null;
}

function showSessionPanel(): void {
  connectPanel.classList.add('hidden');
  sessionPanel.classList.remove('hidden');
}

/** Only one renderer is visible at a time: a <video> track or a decoded <canvas>. */
function showRenderer(which: 'video' | 'canvas'): void {
  video.classList.toggle('hidden', which !== 'video');
  canvas.classList.toggle('hidden', which !== 'canvas');
}

function teardown(): void {
  detachInput?.();
  detachInput = null;
  decoder?.close();
  decoder = null;
  terminal?.dispose();
  terminal = null;
  terminalHost.replaceChildren(); // drop xterm's DOM so the next session starts clean
  terminalPanel.classList.add('hidden');
  terminalButton.classList.remove('active');
  files?.dispose();
  files = null;
  filesPanel.classList.add('hidden');
  fileList.replaceChildren();
  fileRefreshButton.disabled = false;
  fileUpButton.disabled = false;
  setStatus(fileStatus, '');
  audit?.dispose();
  audit = null;
  auditPanel.classList.add('hidden');
  auditButton.classList.remove('active');
  auditList.replaceChildren();
  auditCount.textContent = '0';
  video.srcObject = null;
  cameraVideo.srcObject = null;
  remoteAudio.srcObject = null;
  control = null;
  screenStreaming = false;
  cameraStreaming = false;
  micStreaming = false;
  connectionIndicator.className = 'indicator';
  connectionIndicator.textContent = 'Connecting…';
  streamButton.textContent = 'Start streaming';
  streamButton.classList.remove('active');
  cameraButton.textContent = 'Camera';
  cameraButton.classList.remove('active');
  cameraPanel.classList.add('hidden');
  cameraSwitchButton.classList.add('hidden');
  micButton.textContent = 'Mic';
  micButton.classList.remove('active');
  stageIdle.classList.remove('hidden');
  session = null;
  connectButton.disabled = false;
  connectPanel.classList.remove('hidden');
  sessionPanel.classList.add('hidden');
}

function setStatus(target: HTMLElement, text: string, isError = false): void {
  target.textContent = text;
  target.classList.toggle('error', isError);
}

function requireElement<T extends HTMLElement>(id: string): T {
  const element = document.getElementById(id);
  if (!element) throw new Error(`missing element #${id}`);
  return element as T;
}

// Closing the tab mid-session should end it rather than leaving the phone
// streaming to nobody until the socket times out.
window.addEventListener('beforeunload', () => session?.close());

// Everything below runs after all listeners are registered above, which matters:
// the grouping formatter and the auto-connect both depend on them existing.
sessionInput.dispatchEvent(new Event('input')); // apply the 3-3-3 grouping
if (params.get('auto') === '1') start();
